import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, Copy, FileSignature, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function EInvoicePreviewDialog({ open, onClose, invoiceId, onGenerated }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    useEffect(() => {
        if (!open || !invoiceId) return;
        setLoading(true);
        setShowRawJson(false);
        api.get(`/e-invoices/${invoiceId}/preview`)
            .then(({ data }) => setData(data))
            .catch(() => toast.error("Failed to load preview"))
            .finally(() => setLoading(false));
    }, [open, invoiceId]);

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(data?.payload || {}, null, 2));
        toast.success("JSON copied to clipboard");
    };

    const generate = async () => {
        setSubmitting(true);
        try {
            await api.post(`/e-invoices/${invoiceId}/generate`);
            toast.success("IRN generated successfully");
            onGenerated?.();
            onClose();
        } catch (e) {
            const msg = e.response?.data?.detail?.message || e.response?.data?.detail || "Generation failed";
            toast.error(typeof msg === "string" ? msg : "Generation failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-4xl" data-testid="einvoice-preview-dialog">
                <DialogHeader>
                    <div className="text-[10px] font-semibold tracking-[0.18em] text-[#a68a56]">NIC IRP / E-INVOICE</div>
                    <DialogTitle className="text-xl">Payload preview & validation</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center gap-2 text-muted-foreground p-8 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" /> Building schema-1.1 payload…
                    </div>
                ) : data ? (
                    <div className="space-y-4">
                        {/* Validation summary */}
                        <div className={`rounded-md border p-3 flex items-start gap-3 ${data.valid ? "border-[#1f6c47]/30 bg-[#1f6c47]/8" : "border-[#b4233a]/40 bg-[#b4233a]/8"}`}>
                            {data.valid ? <CheckCircle2 className="w-5 h-5 text-[#1f6c47] mt-0.5" /> : <XCircle className="w-5 h-5 text-[#b4233a] mt-0.5" />}
                            <div className="flex-1">
                                <div className="font-semibold text-sm">
                                    {data.valid ? "Schema valid — ready for IRN generation" : `${data.error_count} error${data.error_count !== 1 ? "s" : ""} prevent IRN generation`}
                                </div>
                                {data.warning_count > 0 && (
                                    <div className="text-[12px] text-muted-foreground mt-0.5">{data.warning_count} warning{data.warning_count !== 1 ? "s" : ""} — review before submission</div>
                                )}
                            </div>
                        </div>

                        {/* Issues list */}
                        {(data.issues || []).length > 0 && (
                            <div className="border border-border rounded-md divide-y divide-border max-h-[200px] overflow-y-auto">
                                {data.issues.map((iss, idx) => (
                                    <div key={idx} className="px-3 py-2 flex items-start gap-2.5 text-[12.5px]">
                                        {iss.severity === "error" ? <XCircle className="w-4 h-4 text-[#b4233a] mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-[#c97a3a] mt-0.5 shrink-0" />}
                                        <div>
                                            <span className="font-mono text-[10.5px] text-muted-foreground mr-2">{iss.code}</span>
                                            <span>{iss.message}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* JSON payload */}
                        {data.payload && <FormattedEInvoice payload={data.payload} />}

                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <button
                                    type="button"
                                    onClick={() => setShowRawJson((v) => !v)}
                                    className="flex items-center gap-1 text-[10.5px] tracking-[0.16em] font-semibold text-muted-foreground hover:text-foreground transition-colors"
                                    data-testid="einv-toggle-raw"
                                >
                                    {showRawJson ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                    RAW PAYLOAD (NIC SCHEMA 1.1)
                                </button>
                                {showRawJson && (
                                    <Button size="sm" variant="outline" onClick={copyJson} data-testid="einv-copy-json">
                                        <Copy className="w-3.5 h-3.5 mr-1" /> Copy JSON
                                    </Button>
                                )}
                            </div>
                            {showRawJson && (
                                <pre className="bg-[#0f1424] text-[#e5e7eb] text-[11.5px] leading-[1.55] rounded-md p-4 max-h-[360px] overflow-auto font-mono" data-testid="einv-raw-json">
{JSON.stringify(data.payload, null, 2)}
                                </pre>
                            )}
                        </div>
                    </div>
                ) : null}

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={onClose}>Close</Button>
                    <Button onClick={generate} disabled={submitting || !data?.valid} data-testid="einv-generate-btn">
                        {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : <><FileSignature className="w-4 h-4 mr-1.5" /> Generate IRN</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/* ---------- Formatted E-Invoice document (human-readable) ---------- */
function FormattedEInvoice({ payload }) {
    const seller = payload.SellerDtls || {};
    const buyer = payload.BuyerDtls || {};
    const doc = payload.DocDtls || {};
    const tran = payload.TranDtls || {};
    const val = payload.ValDtls || {};
    const items = payload.ItemList || [];
    return (
        <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="einv-formatted">
            <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
                <div>
                    <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Tax invoice · {tran.SupTyp || "B2B"} · GST</div>
                    <div className="text-[17px] font-semibold mt-0.5">
                        {doc.Typ === "CRN" ? "Credit Note" : doc.Typ === "DBN" ? "Debit Note" : "Invoice"} <span className="font-mono text-[14px] text-muted-foreground">#{doc.No}</span>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Invoice date</div>
                    <div className="text-[13px] font-mono tabular-nums">{doc.Dt || "—"}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-b border-border">
                <PartyBlock kicker="Seller (from)" party={seller} />
                <div className="border-l border-border">
                    <PartyBlock kicker="Buyer (to)" party={buyer} />
                </div>
            </div>

            <div className="p-4">
                <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-2">Line items</div>
                <div className="border border-border rounded-md overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="bg-muted/40">
                            <tr>
                                <th className="text-left px-2 py-1.5 font-semibold">#</th>
                                <th className="text-left px-2 py-1.5 font-semibold">Description</th>
                                <th className="text-left px-2 py-1.5 font-semibold">HSN/SAC</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Qty</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Rate</th>
                                <th className="text-right px-2 py-1.5 font-semibold">GST %</th>
                                <th className="text-right px-2 py-1.5 font-semibold">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((it, i) => (
                                <tr key={i} className="border-t border-border">
                                    <td className="px-2 py-1.5 text-muted-foreground">{it.SlNo}</td>
                                    <td className="px-2 py-1.5">{it.PrdDesc}</td>
                                    <td className="px-2 py-1.5 font-mono text-[11px]">{it.HsnCd}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{it.Qty} <span className="text-muted-foreground">{it.Unit}</span></td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(it.UnitPrice)}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{it.GstRt}%</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmtMoney(it.TotItemVal)}</td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="px-2 py-4 text-center text-muted-foreground">No line items</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="text-[11.5px] text-muted-foreground leading-relaxed">
                        <div><span className="uppercase tracking-wider">Reverse charge:</span> <span className="font-mono">{tran.RegRev || "N"}</span></div>
                        <div><span className="uppercase tracking-wider">Supply type:</span> <span className="font-mono">{tran.SupTyp}</span></div>
                        <div><span className="uppercase tracking-wider">Place of supply:</span> <span className="font-mono">{buyer.Pos || "—"}</span></div>
                    </div>
                    <div className="text-[12.5px] space-y-1 min-w-0">
                        <TotalRow label="Taxable value" value={val.AssVal} />
                        {val.CgstVal > 0 && <TotalRow label="CGST" value={val.CgstVal} />}
                        {val.SgstVal > 0 && <TotalRow label="SGST" value={val.SgstVal} />}
                        {val.IgstVal > 0 && <TotalRow label="IGST" value={val.IgstVal} />}
                        {val.CesVal > 0 && <TotalRow label="Cess" value={val.CesVal} />}
                        {val.OthChrg > 0 && <TotalRow label="Other charges" value={val.OthChrg} />}
                        {val.RndOffAmt !== 0 && <TotalRow label="Round off" value={val.RndOffAmt} />}
                        <div className="flex justify-between pt-2 mt-2 border-t border-border font-semibold text-[13.5px]">
                            <span>Total invoice value</span>
                            <span className="tabular-nums" data-testid="einv-total-value">{fmtMoney(val.TotInvVal)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PartyBlock({ kicker, party }) {
    const addr = [party.Addr1, party.Addr2].filter(Boolean).join(", ");
    return (
        <div className="p-4">
            <div className="text-[9.5px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">{kicker}</div>
            <div className="mt-1 text-[14px] font-semibold">{party.LglNm || party.TrdNm || "—"}</div>
            {party.TrdNm && party.TrdNm !== party.LglNm && <div className="text-[12px] text-muted-foreground">{party.TrdNm}</div>}
            {party.Gstin && <div className="text-[11.5px] font-mono mt-1">GSTIN {party.Gstin}</div>}
            <div className="text-[12px] text-muted-foreground mt-1">
                {addr}
                {(party.Loc || party.Pin) && <div>{[party.Loc, party.Pin].filter(Boolean).join(" · ")}</div>}
                {party.Stcd && <div>State code: <span className="font-mono">{party.Stcd}</span></div>}
            </div>
        </div>
    );
}

function TotalRow({ label, value }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums">{fmtMoney(value)}</span>
        </div>
    );
}
